// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Modal, Button, Icon, Message, Form, Segment, Header, Divider, Table } from 'semantic-ui-react';
import type { PolicyType } from '../api/policyTypeApi';
import type { QuoteTerm, QuoteTermOption, QuoteTermAllocationBand } from '../api/quoteTermApi';

interface Props {
  open: boolean;
  providerId: string;
  products: PolicyType[];
  existing?: QuoteTerm | null;
  onClose: () => void;
  onSave: (body: QuoteTerm) => Promise<void>;
}

const BASIS_OPTIONS = [
  { key: 'GROSS', text: 'Gross (banded)', value: 'GROSS' },
  { key: 'NET', text: 'Net (banded)', value: 'NET' },
  { key: 'FLAT', text: 'Flat', value: 'FLAT' },
];

const FEE_FREQUENCY_OPTIONS = [
  { key: 'ANNUAL', text: 'Per annum', value: 'ANNUAL' },
  { key: 'MONTHLY', text: 'Monthly', value: 'MONTHLY' },
  { key: 'QUARTERLY', text: 'Quarterly', value: 'QUARTERLY' },
  { key: 'ONE_OFF', text: 'One-off', value: 'ONE_OFF' },
];

type MetadataEntry = { key: string; value: string };

/**
 * Editor working shape: charge schedules are held as raw strings so commas / % /
 * slashes survive typing (parsed on save); provider-specific extras are edited as
 * key-value rows and collapsed into the metadata Record on save.
 */
type OptionDraft = Omit<QuoteTermOption, 'exitCharges' | 'commissionClawback' | 'metadata'> & {
  exitChargesText: string;
  commissionClawbackText: string;
  metadataEntries: MetadataEntry[];
};

const numOrNull = (v: string): number | null => (v === '' || v == null ? null : Number(v));
const scheduleToText = (v?: number[]): string => (v && v.length ? v.join(', ') : '');
/** Tolerant parse: split on comma / slash / whitespace, strip %, keep valid numbers. */
const parseSchedule = (v: string): number[] =>
  v
    .split(/[,/\s]+/)
    .map((s) => s.replace('%', '').trim())
    .filter((s) => s !== '')
    .map(Number)
    .filter((n) => !Number.isNaN(n));

const entriesToRecord = (entries: MetadataEntry[]): Record<string, unknown> | null => {
  const record: Record<string, unknown> = {};
  for (const { key, value } of entries) {
    const k = key.trim();
    if (k) record[k] = value;
  }
  return Object.keys(record).length ? record : null;
};

const emptyOption = (): OptionDraft => ({
  optionCode: '',
  allocationBasis: 'GROSS',
  bands: [{ allocationRate: 100 }],
  exitChargesText: '',
  commissionClawbackText: '',
  metadataEntries: [],
});

const toDraft = (o: QuoteTermOption): OptionDraft => ({
  ...o,
  exitChargesText: scheduleToText(o.exitCharges),
  commissionClawbackText: scheduleToText(o.commissionClawback),
  metadataEntries: Object.entries(o.metadata ?? {}).map(([key, value]) => ({ key, value: String(value) })),
});

export default function QuoteTermEditorModal({ open, providerId, products, existing, onClose, onSave }: Props) {
  const [productId, setProductId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('EUR');
  const [notes, setNotes] = useState('');
  const [options, setOptions] = useState<OptionDraft[]>([emptyOption()]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existing) {
      setProductId(existing.productId);
      setCurrencyCode(existing.currencyCode ?? 'EUR');
      setNotes(existing.notes ?? '');
      setOptions(existing.options?.length ? existing.options.map(toDraft) : [emptyOption()]);
    } else {
      setProductId('');
      setCurrencyCode('EUR');
      setNotes('');
      setOptions([emptyOption()]);
    }
    setError(null);
  }, [existing, open]);

  const productOptions = products.map((p) => ({ key: p.id!, text: `${p.name} (${p.category})`, value: p.id! }));

  const setOption = (idx: number, patch: Partial<OptionDraft>) => {
    setOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  };
  const setBand = (oi: number, bi: number, patch: Partial<QuoteTermAllocationBand>) => {
    setOptions((prev) => prev.map((o, i) => {
      if (i !== oi) return o;
      const bands = (o.bands ?? []).map((b, j) => (j === bi ? { ...b, ...patch } : b));
      return { ...o, bands };
    }));
  };
  const setMetaEntry = (oi: number, mi: number, patch: Partial<MetadataEntry>) => {
    setOptions((prev) => prev.map((o, i) => {
      if (i !== oi) return o;
      const metadataEntries = (o.metadataEntries ?? []).map((e, j) => (j === mi ? { ...e, ...patch } : e));
      return { ...o, metadataEntries };
    }));
  };

  const handleSave = async () => {
    setError(null);
    if (!productId) { setError('Select a product'); return; }
    const mappedOptions: QuoteTermOption[] = options.map(
      ({ exitChargesText, commissionClawbackText, metadataEntries, ...rest }) => ({
        ...rest,
        exitCharges: parseSchedule(exitChargesText),
        commissionClawback: parseSchedule(commissionClawbackText),
        metadata: entriesToRecord(metadataEntries),
      }),
    );
    const body: QuoteTerm = {
      ...(existing ?? {}),
      providerId,
      productId,
      currencyCode,
      isActive: existing?.isActive ?? true,
      notes,
      options: mappedOptions,
    };
    setSaving(true);
    try {
      await onSave(body);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="table" /> {existing ? 'Edit' : 'Add'} Terms &amp; Quotes
      </Modal.Header>
      <Modal.Content scrolling>
        {error && <Message negative><Message.Header>Error</Message.Header><p>{error}</p></Message>}
        <Form>
          <Form.Group widths="equal">
            <Form.Select
              label="Product" placeholder="Choose a product" required search
              options={productOptions} value={productId}
              onChange={(_e, d) => setProductId(d.value as string)}
            />
            <Form.Input
              label="Currency" value={currencyCode} maxLength={3}
              onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
            />
          </Form.Group>
          <Form.TextArea label="Notes" value={notes} rows={2} onChange={(_e, d) => setNotes(String(d.value ?? ''))} />
        </Form>

        <Divider />
        <Header as="h4">Options</Header>
        {options.map((opt, oi) => (
          <Segment key={oi} data-testid={`option-${oi}`}>
            <Form>
              <Form.Group widths="equal">
                <Form.Input
                  label="Option code" value={opt.optionCode} required
                  onChange={(e) => setOption(oi, { optionCode: e.target.value })}
                />
                <Form.Input
                  label="Name" value={opt.optionName ?? ''}
                  onChange={(e) => setOption(oi, { optionName: e.target.value })}
                />
                <Form.Select
                  label="Allocation basis" options={BASIS_OPTIONS} value={opt.allocationBasis}
                  onChange={(_e, d) => setOption(oi, { allocationBasis: d.value as QuoteTermOption['allocationBasis'] })}
                />
              </Form.Group>

              <Header as="h5">Fund charges (%)</Header>
              <Form.Group widths="equal">
                <Form.Input label="Standard" type="number" step="0.01" value={opt.standardFundCharge ?? ''}
                  onChange={(e) => setOption(oi, { standardFundCharge: numOrNull(e.target.value) })} />
                <Form.Input label="Passive" type="number" step="0.01" value={opt.passiveFundCharge ?? ''}
                  onChange={(e) => setOption(oi, { passiveFundCharge: numOrNull(e.target.value) })} />
                <Form.Input label="Fixed / My Future allocation" type="number" step="0.01"
                  value={opt.fixedAllocationFundCharge ?? ''}
                  onChange={(e) => setOption(oi, { fixedAllocationFundCharge: numOrNull(e.target.value) })} />
              </Form.Group>

              {opt.allocationBasis === 'FLAT' ? (
                <Form.Input
                  label="Flat allocation rate (%)" type="number" value={opt.flatAllocationRate ?? ''}
                  onChange={(e) => setOption(oi, { flatAllocationRate: numOrNull(e.target.value) })}
                />
              ) : (
                <>
                  <Header as="h5">Allocation bands</Header>
                  <Table compact size="small">
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Contribution min</Table.HeaderCell>
                        <Table.HeaderCell>Contribution max</Table.HeaderCell>
                        <Table.HeaderCell>Allocation rate %</Table.HeaderCell>
                        <Table.HeaderCell />
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {(opt.bands ?? []).map((b, bi) => (
                        <Table.Row key={bi}>
                          <Table.Cell>
                            <input type="number" value={b.contributionMin ?? ''}
                              onChange={(e) => setBand(oi, bi, { contributionMin: numOrNull(e.target.value) })} />
                          </Table.Cell>
                          <Table.Cell>
                            <input type="number" value={b.contributionMax ?? ''}
                              onChange={(e) => setBand(oi, bi, { contributionMax: numOrNull(e.target.value) })} />
                          </Table.Cell>
                          <Table.Cell>
                            <input type="number" value={b.allocationRate ?? ''}
                              onChange={(e) => setBand(oi, bi, { allocationRate: Number(e.target.value) })} />
                          </Table.Cell>
                          <Table.Cell>
                            <Button icon="trash" size="mini" color="red" type="button"
                              onClick={() => setOption(oi, { bands: (opt.bands ?? []).filter((_, j) => j !== bi) })} />
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                  <Button size="mini" type="button"
                    onClick={() => setOption(oi, { bands: [...(opt.bands ?? []), { allocationRate: 100 }] })}>
                    <Icon name="plus" /> Add band
                  </Button>
                </>
              )}

              <Header as="h5">Commission (%)</Header>
              <Form.Group widths="equal">
                <Form.Input label="Gross initial — min" type="number" step="0.01" value={opt.initialCommissionMin ?? ''}
                  onChange={(e) => setOption(oi, { initialCommissionMin: numOrNull(e.target.value) })} />
                <Form.Input label="Gross initial — max" type="number" step="0.01" value={opt.initialCommissionMax ?? ''}
                  onChange={(e) => setOption(oi, { initialCommissionMax: numOrNull(e.target.value) })} />
                <Form.Input label="Gross initial — fixed" type="number" step="0.01" value={opt.initialCommissionFixed ?? ''}
                  onChange={(e) => setOption(oi, { initialCommissionFixed: numOrNull(e.target.value) })} />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input label="Fund-based — min" type="number" step="0.01" value={opt.fundBasedCommissionMin ?? ''}
                  onChange={(e) => setOption(oi, { fundBasedCommissionMin: numOrNull(e.target.value) })} />
                <Form.Input label="Fund-based — max" type="number" step="0.01" value={opt.fundBasedCommissionMax ?? ''}
                  onChange={(e) => setOption(oi, { fundBasedCommissionMax: numOrNull(e.target.value) })} />
                <Form.Input label="Accrual rate" placeholder="e.g. 0.38% × Term" value={opt.accrualRate ?? ''}
                  onChange={(e) => setOption(oi, { accrualRate: e.target.value })} />
              </Form.Group>

              <Header as="h5">Contribution &amp; term</Header>
              <Form.Group widths="equal">
                <Form.Input label="Min contribution" type="number" value={opt.minContribution ?? ''}
                  onChange={(e) => setOption(oi, { minContribution: numOrNull(e.target.value) })} />
                <Form.Input label="Max contribution" type="number" value={opt.maxContribution ?? ''}
                  onChange={(e) => setOption(oi, { maxContribution: numOrNull(e.target.value) })} />
                <Form.Input label="Min term (years)" type="number" value={opt.minTermYears ?? ''}
                  onChange={(e) => setOption(oi, { minTermYears: numOrNull(e.target.value) })} />
                <Form.Input label="Max NRA for commission" type="number" value={opt.maxNraForCommission ?? ''}
                  onChange={(e) => setOption(oi, { maxNraForCommission: numOrNull(e.target.value) })} />
              </Form.Group>

              <Header as="h5">Exit &amp; clawback schedules</Header>
              <Form.Group widths="equal">
                <Form.Input label="Exit charges (%)" placeholder="e.g. 5, 5, 5, 3, 2"
                  value={opt.exitChargesText}
                  onChange={(e) => setOption(oi, { exitChargesText: e.target.value })} />
                <Form.Input label="Commission clawback (%)" placeholder="e.g. 100, 75, 50, 25"
                  value={opt.commissionClawbackText}
                  onChange={(e) => setOption(oi, { commissionClawbackText: e.target.value })} />
              </Form.Group>

              <Header as="h5">Fees &amp; terms</Header>
              <Form.Group widths="equal">
                <Form.Input label="Policy fee amount" type="number" value={opt.policyFeeAmount ?? ''}
                  onChange={(e) => setOption(oi, { policyFeeAmount: numOrNull(e.target.value) })} />
                <Form.Select label="Policy fee frequency" options={FEE_FREQUENCY_OPTIONS} clearable
                  value={opt.policyFeeFrequency ?? ''}
                  onChange={(_e, d) => setOption(oi, { policyFeeFrequency: (d.value as string) || null })} />
                <Form.Input label="Fund switches" placeholder="e.g. Free" value={opt.fundSwitches ?? ''}
                  onChange={(e) => setOption(oi, { fundSwitches: e.target.value })} />
                <Form.Input label="Customer withdrawals" placeholder="e.g. Up to 7.5% without penalty"
                  value={opt.customerWithdrawals ?? ''}
                  onChange={(e) => setOption(oi, { customerWithdrawals: e.target.value })} />
              </Form.Group>

              <Header as="h5">Additional fields (provider-specific)</Header>
              {(opt.metadataEntries ?? []).map((entry, mi) => (
                <Form.Group key={mi} widths="equal">
                  <Form.Input label="Field name" placeholder="e.g. S-code" value={entry.key}
                    onChange={(e) => setMetaEntry(oi, mi, { key: e.target.value })} />
                  <Form.Input label="Value" placeholder="e.g. S520" value={entry.value}
                    onChange={(e) => setMetaEntry(oi, mi, { value: e.target.value })} />
                  <Form.Field>
                    <label>&nbsp;</label>
                    <Button size="small" basic color="red" type="button"
                      onClick={() => setOption(oi, {
                        metadataEntries: (opt.metadataEntries ?? []).filter((_, j) => j !== mi),
                      })}>
                      Remove
                    </Button>
                  </Form.Field>
                </Form.Group>
              ))}
              <Button size="mini" type="button"
                onClick={() => setOption(oi, { metadataEntries: [...(opt.metadataEntries ?? []), { key: '', value: '' }] })}>
                <Icon name="plus" /> Add field
              </Button>

              {options.length > 1 && (
                <div style={{ marginTop: '1rem' }}>
                  <Button size="small" color="red" basic type="button"
                    onClick={() => setOptions((prev) => prev.filter((_, i) => i !== oi))}>
                    <Icon name="trash" /> Remove option
                  </Button>
                </div>
              )}
            </Form>
          </Segment>
        ))}
        <Button type="button" onClick={() => setOptions((prev) => [...prev, emptyOption()])}>
          <Icon name="plus" /> Add option
        </Button>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button primary onClick={handleSave} loading={saving}><Icon name="save" /> Save</Button>
      </Modal.Actions>
    </Modal>
  );
}
